import { Formatting } from "../../formatting";
import { Attribute } from "../../metadata";
import { ICurrency, IDatasetEventListeners, IRecordChange } from "../interfaces";
import { Record } from "./Record/Record";
import { IRecord } from "./interfaces";

export abstract class DataProvider {
    private _loading: boolean = true;
    private _searchQuery: string = "";
    private _changes: Map<string, IRecordChange> = new Map();
    private _sorting: ComponentFramework.PropertyHelper.DataSetApi.SortStatus[] = [];
    private _filtering: ComponentFramework.PropertyHelper.DataSetApi.FilterExpression | null = null;
    private _linking: ComponentFramework.PropertyHelper.DataSetApi.LinkEntityExposedExpression[] = [];
    private _currencies: ICurrency[] = [];
    //@ts-ignore registered through registerEventHandler
    private _eventListeners: {
        [K in keyof IDatasetEventListeners]: IDatasetEventListeners[K][];
    } = {};

    constructor() {
        this.addEventListener('onRecordColumnValueChanged', (record, columnName: string) => {
            this._triggerChanges(record);
        });
    }
    public setSorting(sorting: ComponentFramework.PropertyHelper.DataSetApi.SortStatus[]) {
        this._sorting = sorting;
    }
    public setFiltering(filtering: ComponentFramework.PropertyHelper.DataSetApi.FilterExpression | null): void {
        this._filtering = filtering;
    }
    public setLinking(expr: ComponentFramework.PropertyHelper.DataSetApi.LinkEntityExposedExpression[]): void {
        this._linking = expr;
    }
    public getFiltering(): ComponentFramework.PropertyHelper.DataSetApi.FilterExpression | null {
        return this._filtering;
    }
    public getSorting(): ComponentFramework.PropertyHelper.DataSetApi.SortStatus[] {
        return this._sorting;
    }
    public getSearchQuery() {
        return this._searchQuery;
    }
    public setSearchQuery(query?: string) {
        this._searchQuery = query ?? "";
    }
    public getLinking() {
        return this._linking;
    }
    public isLoading() {
        return this._loading;
    }
    public setLoading(value: boolean) {
        this._loading = value;
    }
    public getCurrencies() {
        return this._currencies;
    }
    public setCurrencies(currencies: ICurrency[]) {
        this._currencies = currencies;
    }
    public getRecordCurrencySymbol(record: IRecord, columnName: string): string {
        if (this.getCurrencies().length === 0) {
            return Formatting.Get().numberFormattingInfo.currencySymbol;
        }
        const linkedEntityAlias = Attribute.GetLinkedEntityAlias(columnName);
        const currencyLookup: ComponentFramework.EntityReference = record.getValue(`${linkedEntityAlias ? linkedEntityAlias + "." : ""}transactioncurrencyid`)?.[0];
        const currencyId = currencyLookup?.id?.guid ?? record.getRawData?.()!['_transactioncurrencyid_value'];

        if (currencyId) {
            const matchedCurrency = this.getCurrencies().find(currency => currency.transactioncurrencyid === currencyId);
            if (matchedCurrency) {
                return matchedCurrency.currencysymbol;
            }
        }
        const defaultCurrencies = this.getCurrencies().filter(currency => ['CZK', 'USD'].includes(currency.currencyname));
        const selectedCurrency = defaultCurrencies.find(currency => currency.currencyname === 'CZK')
            ?? defaultCurrencies.find(currency => currency.currencyname === 'USD')
            ?? this.getCurrencies()[0];

        return selectedCurrency.currencysymbol;
    }

    public addEventListener<K extends keyof IDatasetEventListeners>(event: K, eventListener: IDatasetEventListeners[K]) {
        if (!this._eventListeners[event]) {
            this._eventListeners[event] = [];
        }
        this._eventListeners[event].push(eventListener);
    }

    public hasInvalidChanges(): boolean {
        return [...this._changes.values()].some(change => change.columns.find(x => x.isValid === false));
    }
    public isDirty(): boolean {
        return this._changes.size > 0;
    }
    public getChanges() {
        const result: {
            [recordId: string]: IRecordChange & {
                record: Record
            }
        } = {};
        [...this._changes.entries()].map(([recordId, change]) => {
            result[recordId] = {
                record: change.record as Record,
                columns: change.columns
            };
        });
        return result;
    }

    public clearChanges() {
        [...this._changes.values()].map(change => {
            change.record.clearChanges?.();
        });
    }
    public fireEventListeners<K extends keyof IDatasetEventListeners>(event: K, ...par: Parameters<IDatasetEventListeners[K]>): Array<ReturnType<IDatasetEventListeners[K]>> {
        //@ts-ignore - typings
        return this._eventListeners[event]?.map(callback => callback(...par)) ?? [];
    }
    private _triggerChanges(record: IRecord) {
        const recordId = record.getRecordId();
        const changes = record.getChanges?.() ?? [];
        if (changes.length === 0) {
            this._changes.delete(recordId);
        }
        else {
            this._changes.set(recordId, {
                columns: changes,
                record: record
            });
        }
    }
}